import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  useCompany,
  useCompanyBalance,
  useCompanyCredit,
  CompanyBalanceResponse,
  CompanyCreditResponse,
  CompanyBalanceData,
  CompanyCredit,
  CompanyData,
} from '../../../services/api/companies'
import { AsyncStates } from '../../../components'
import { View } from './View'
import {
  APIError,
  isNotFoundError,
  Response,
} from '../../../services/api/client/types'
import { transformError } from '../../../services/api/client/useAPI'

type CompanyInfo = {
  company: CompanyData
  balance: CompanyBalanceData
  credit?: CompanyCredit
}
type CompanyInfoResponse = Response<CompanyInfo>

export const Container: React.FunctionComponent = () => {
  const { id } = useParams()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<APIError | null>(null)
  const [response, setResponse] = useState<CompanyInfoResponse | null>(null)

  const [getCompany] = useCompany(id)
  const [getCompanyBalance] = useCompanyBalance(id)
  const [getCompanyCredit] = useCompanyCredit(id)

  const getCompanyInfo = useCallback(() => {
    return getCompany().then(({ data: company }) => {
      return Promise.all<
        CompanyBalanceResponse,
        CompanyCreditResponse | undefined
      >([
        getCompanyBalance(),
        getCompanyCredit().catch((error) => {
          if (isNotFoundError(error)) {
            return Promise.resolve(undefined)
          } else {
            throw error
          }
        }),
      ]).then(([{ data: balance }, creditResponse]) => {
        setResponse({
          data: {
            company: company,
            balance: balance,
            credit: creditResponse?.data,
          },
        })
      })
    })
  }, [getCompany, getCompanyBalance, getCompanyCredit])

  const loadCompany = useCallback(() => {
    setIsLoading(true)
    setError(null)

    getCompanyInfo()
      .catch((error) => {
        const apiError = transformError(error)
        setError(apiError)
        throw apiError
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [getCompanyInfo])

  useEffect(() => {
    loadCompany()
  }, [loadCompany])

  return (
    <AsyncStates<CompanyInfo>
      isLoading={isLoading}
      error={error}
      errorMessage="Не удалось загрузить данные по компании"
      reload={() => loadCompany()}
      response={response}
      renderData={({ company, balance, credit }) => (
        <View
          company={company}
          balance={balance}
          credit={credit}
          onCompanyBalanceUpdate={() => loadCompany()}
        />
      )}
    />
  )
}
